import { Minus, Plus, Package, X } from "lucide-react";

type PosItemThumbnailProps = {
  imageUrl?: string | null;
  alt: string;
};

export function PosItemThumbnail({ imageUrl, alt }: PosItemThumbnailProps) {
  return (
    <div className="w-14 h-14 md:w-16 md:h-16 flex-shrink-0 rounded-md overflow-hidden bg-white">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={alt}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Package className="h-5 w-5 md:h-6 md:w-6 text-gray-400" />
        </div>
      )}
    </div>
  );
}

type PosQuantityStepperProps = {
  quantity: number;
  onIncrease: () => void;
  onDecrease: () => void;
  decreaseDisabled?: boolean;
};

export function PosQuantityStepper({
  quantity,
  onIncrease,
  onDecrease,
  decreaseDisabled = false,
}: PosQuantityStepperProps) {
  return (
    <div className="flex items-center gap-0.5 md:gap-1 border border-gray-300 rounded-md bg-white">
      <button
        type="button"
        onClick={onDecrease}
        disabled={decreaseDisabled}
        className="p-1 md:p-1.5 hover:bg-gray-100 transition-colors rounded-l-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Minus className="h-3 w-3" />
      </button>
      <span className="w-6 md:w-8 text-center font-medium text-xs md:text-sm">
        {quantity}
      </span>
      <button
        type="button"
        onClick={onIncrease}
        className="p-1 md:p-1.5 hover:bg-gray-100 transition-colors rounded-r-md"
      >
        <Plus className="h-3 w-3" />
      </button>
    </div>
  );
}

type PosItemRowProps = {
  name: string;
  imageUrl?: string | null;
  price: number;
  quantity: number;
  onIncrease: () => void;
  onDecrease: () => void;
  decreaseDisabled?: boolean;
  onRemove?: () => void;
};

export function PosItemRow({
  name,
  imageUrl,
  price,
  quantity,
  onIncrease,
  onDecrease,
  decreaseDisabled = false,
  onRemove,
}: PosItemRowProps) {
  const lineTotal = price * quantity;

  return (
    <div className="flex gap-2 md:gap-3 p-2 md:p-3 rounded-lg border border-gray-200 bg-gray-50/50 hover:bg-gray-50 transition-colors">
      <PosItemThumbnail imageUrl={imageUrl} alt={name} />

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-start justify-between gap-1.5 md:gap-2 mb-0.5 md:mb-1">
          <p className="font-medium text-xs md:text-sm text-foreground line-clamp-1 flex-1">
            {name}
          </p>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="flex-shrink-0 p-0.5 md:p-1 rounded-md hover:bg-red-50 text-red-500 transition-colors"
            >
              <X className="h-3.5 w-3.5 md:h-4 md:w-4" />
            </button>
          )}
        </div>

        <p className="text-[10px] md:text-xs text-muted-foreground">
          ${price.toFixed(2)} each
        </p>

        <div className="flex items-center justify-between mt-1.5 md:mt-2">
          <p className="font-semibold text-sm md:text-base text-primary">
            ${lineTotal.toFixed(2)}
          </p>

          <PosQuantityStepper
            quantity={quantity}
            onIncrease={onIncrease}
            onDecrease={onDecrease}
            decreaseDisabled={decreaseDisabled}
          />
        </div>
      </div>
    </div>
  );
}
